// Components
import { Select, MenuItem, styled } from '@mui/material';

// Modern Styled Select Component with Dark Mode Support
const CustomSelect = styled(Select, {
    shouldForwardProp: (prop) => !['isDarkMode', 'darkTheme', 'primaryColor'].includes(prop), // Prevent custom props from being passed to the DOM
})(({ theme, isDarkMode, darkTheme, primaryColor }) => ({
    borderRadius: 12,
    backgroundColor: isDarkMode ? darkTheme?.headerBg : '#ffffff',
    color: isDarkMode ? darkTheme?.textPrimary : 'inherit',
    fontSize: '0.95rem',
    fontWeight: 500,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',

    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? darkTheme?.cardBorder : 'rgba(0, 0, 0, 0.15)',
        borderWidth: '1.5px',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    },

    // Hover state
    '&:hover': {
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor || (isDarkMode ? darkTheme?.accent : '#1976d2'),
            borderWidth: '2px',
        },
    },

    // Focused state
    '&.Mui-focused': {
        backgroundColor: isDarkMode ? darkTheme?.headerBg : '#ffffff',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor || (isDarkMode ? darkTheme?.accent : '#1976d2'),
            borderWidth: '2px',
            boxShadow: isDarkMode 
                ? `0 0 0 3px ${primaryColor ? `${primaryColor}20` : 'rgba(255, 152, 0, 0.15)'}` 
                : `0 0 0 3px ${primaryColor ? `${primaryColor}20` : 'rgba(25, 118, 210, 0.15)'}`,
        },
    },

    // Dropdown icon (only the select arrow, not adornments)
    '& .MuiSelect-icon': {
        color: isDarkMode ? darkTheme?.textSecondary : 'rgba(0, 0, 0, 0.54)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s ease',
        fontSize: '1.4rem',
    },

    // Icon rotation when open (only the select arrow)
    '&.Mui-focused .MuiSelect-icon': {
        transform: 'rotate(180deg)',
        color: primaryColor || (isDarkMode ? darkTheme?.accent : '#1976d2'),
    },
    
    // Prevent rotation on adornment icons
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        transition: 'none',
        transform: 'none !important',
    },

    // Disabled state
    '&.Mui-disabled': {
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
        },
    },

    // Input adornment styling
    '& .MuiInputAdornment-root': {
        color: isDarkMode ? darkTheme?.textSecondary : 'rgba(0, 0, 0, 0.54)',
        marginLeft: 0,
        marginRight: '8px',
    },
}));

// Modern Styled MenuItem with Dark Mode Support
const CustomMenuItem = styled(MenuItem, {
    shouldForwardProp: (prop) => !['isDarkMode', 'darkTheme', 'primaryColor'].includes(prop), // Prevent custom props from being passed to the DOM
})(({ theme, isDarkMode, darkTheme, primaryColor }) => ({
    fontSize: '0.95rem',
    fontWeight: 500,
    padding: '12px 16px',
    margin: '4px 8px',
    borderRadius: 8,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    color: isDarkMode ? darkTheme?.textPrimary : 'rgba(0, 0, 0, 0.87)',

    // Hover state
    '&:hover': {
        backgroundColor: isDarkMode
            ? (primaryColor ? `${primaryColor}15` : 'rgba(255, 152, 0, 0.12)')
            : primaryColor
                ? `${primaryColor}15`
                : 'rgba(25, 118, 210, 0.08)',
        transform: 'translateX(4px)',
    },

    // Selected state
    '&.Mui-selected': {
        backgroundColor: isDarkMode
            ? (primaryColor ? `${primaryColor}25` : 'rgba(255, 152, 0, 0.2)')
            : primaryColor
                ? `${primaryColor}25`
                : 'rgba(25, 118, 210, 0.12)',
        color: `${primaryColor || (isDarkMode ? darkTheme?.accent : '#1976d2')} !important`,
        fontWeight: 600,

        '&:hover': {
            backgroundColor: isDarkMode
                ? (primaryColor ? `${primaryColor}30` : 'rgba(255, 152, 0, 0.25)')
                : primaryColor
                    ? `${primaryColor}30`
                    : 'rgba(25, 118, 210, 0.16)',
        },
    },

    // Focus state
    '&.Mui-focusVisible': {
        backgroundColor: isDarkMode
            ? (primaryColor ? `${primaryColor}20` : 'rgba(255, 152, 0, 0.15)')
            : primaryColor
                ? `${primaryColor}20`
                : 'rgba(25, 118, 210, 0.12)',
    },
}));

// Menu Paper styling for dropdown
const getMenuProps = (isDarkMode, darkTheme) => ({
    disableScrollLock: true,
    PaperProps: {
        elevation: 8,
        sx: {
            borderRadius: 3,
            marginTop: 1,
            maxHeight: 400,
            backgroundColor: isDarkMode ? darkTheme?.cardBg : '#ffffff',
            border: isDarkMode ? `1px solid ${darkTheme?.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: isDarkMode 
                ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 1px rgba(255, 255, 255, 0.1)' 
                : '0 8px 24px rgba(0, 0, 0, 0.12)',
            overflow: 'auto',
            
            // Custom scrollbar
            '&::-webkit-scrollbar': {
                width: 8,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
                margin: '8px 0',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                borderRadius: 4,
                '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                },
            },
        },
    },
});

export { CustomSelect, CustomMenuItem, getMenuProps };
export default CustomSelect;
